import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Drawer from "../../../components/Drawer";
import { LoaderSpinner } from "../../../components/LoaderSpinner";
import {
    getVendorAuditLogRequest,
    VendorsLoadingKeys,
} from "../../../slices/managementSlices/vendorsSlice";
import {
    AUDIT_HIDDEN_FIELDS,
    formatAuditFieldLabel,
    formatAuditValue,
} from "./vendorAuditFormat";

const ACTION_LABELS = {
    INSERT: "Created",
    DELETE: "Deleted",
};

const ACTION_TONES = {
    INSERT: "bg-emerald-50 text-emerald-700",
    DELETE: "bg-red-50 text-red-700",
};

const formatChangedAt = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const VendorAuditLogDrawer = ({ isOpen, onClose }) => {
    const dispatch = useDispatch();
    const { loadingKeys, vendorAuditLog } = useSelector((state) => state.vendors);
    const [searchTerm, setSearchTerm] = useState("");

    const loading = loadingKeys.includes(VendorsLoadingKeys.vendorAuditLog);

    useEffect(() => {
        if (isOpen) {
            dispatch(getVendorAuditLogRequest({}));
        } else {
            setSearchTerm("");
        }
    }, [isOpen, dispatch]);

    const rows = useMemo(() => {
        const list = Array.isArray(vendorAuditLog) ? vendorAuditLog : [];
        const flattened = [];
        for (const entry of list) {
            if (entry.action === "UPDATE") {
                const visibleChanges = (entry.changes || []).filter(
                    (change) => !AUDIT_HIDDEN_FIELDS.has(change.field)
                );
                for (const change of visibleChanges) {
                    flattened.push({
                        key: `${entry.id}-${change.field}`,
                        createdAt: entry.createdAt,
                        vendorName: entry.vendorName || "Unknown vendor",
                        field: formatAuditFieldLabel(change.field),
                        oldValue: formatAuditValue(change.oldValue),
                        newValue: formatAuditValue(change.newValue),
                        action: entry.action,
                        userName: entry.userName,
                    });
                }
            } else {
                flattened.push({
                    key: String(entry.id),
                    createdAt: entry.createdAt,
                    vendorName: entry.vendorName || "Unknown vendor",
                    field: null,
                    oldValue: null,
                    newValue: null,
                    action: entry.action,
                    userName: entry.userName,
                });
            }
        }
        const term = searchTerm.trim().toLowerCase();
        if (!term) return flattened;
        return flattened.filter((row) =>
            [row.vendorName, row.userName, row.field, row.oldValue, row.newValue]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(term)
        );
    }, [vendorAuditLog, searchTerm]);

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            width="800px"
            showActionButtons={false}
            title="Supplier Change History"
        >
            <div className="flex flex-col gap-3 px-4 pb-6">
                <p className="text-sm text-gray-500 mt-4">
                    Every change to supplier details is logged here — what changed,
                    who changed it, and when.
                </p>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Filter by vendor, field, or user…"
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
                />
                {loading ? (
                    <div className="flex justify-center py-10">
                        <LoaderSpinner width="48" height="48" border="4" colorSpinner="gray" />
                    </div>
                ) : rows.length === 0 ? (
                    <p className="py-10 text-center text-sm text-gray-500">
                        {searchTerm ? "No entries match your filter." : "No changes logged yet."}
                    </p>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-gray-50 text-gray-600">
                                <tr>
                                    <th className="px-3 py-2 font-medium">When</th>
                                    <th className="px-3 py-2 font-medium">Vendor</th>
                                    <th className="px-3 py-2 font-medium">Field</th>
                                    <th className="px-3 py-2 font-medium">Change</th>
                                    <th className="px-3 py-2 font-medium">By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.key} className="border-t border-gray-100 hover:bg-gray-50/80">
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-500">
                                            {formatChangedAt(row.createdAt)}
                                        </td>
                                        <td className="px-3 py-2 font-medium text-gray-800">
                                            {row.vendorName}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                                            {row.action === "UPDATE" ? row.field : (
                                                <span
                                                    className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${ACTION_TONES[row.action] || "bg-slate-100 text-slate-700"}`}
                                                >
                                                    {ACTION_LABELS[row.action] || row.action}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-gray-700">
                                            {row.action === "UPDATE" ? (
                                                <>
                                                    <span className="break-all">{row.oldValue}</span>
                                                    <span className="mx-1 text-gray-400">→</span>
                                                    <span className="break-all">{row.newValue}</span>
                                                </>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-500">
                                            {row.userName || "System"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Drawer>
    );
};

export default VendorAuditLogDrawer;
